import { useSettings } from '../SettingsProvider';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useSaveSettings } from '@ais-ui/websockets';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  Switch,
  useToast,
} from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import { ShapeSelect } from '../../Shapes/ShapeSelects/ShapeSelect';
import { useSettingsPage } from '../SettingsPageProvider';

const reportFormSchema = z.object({
  show_boundary: z.boolean(),
  show_job_details_in_export: z.boolean(),
  export_all_rows_in_report: z.boolean(),
  interpret_ship_to_be_stopped_below_speed: z
    .union([z.number(), z.literal('')])
    .optional(),
  enable_standard_vessel_filter: z.boolean(),
});

type ReportFormValues = z.infer<typeof reportFormSchema>;

export const ReportForm = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { mutate: mutateSettings } = useSaveSettings();

  const initialValues = useMemo(
    () => ({
      show_boundary: settings['ribbonPlugin.settings'].report_show_boundary,
      show_job_details_in_export:
        settings['ribbonPlugin.settings'].report_show_job_details,
      export_all_rows_in_report:
        settings['ribbonPlugin.settings'].report_export_all,
      interpret_ship_to_be_stopped_below_speed:
        settings['ribbonPlugin.settings'].report_ship_stopped_speed ?? '',
      enable_standard_vessel_filter:
        settings['ribbonPlugin.settings'].report_exclude_all_non_vessels,
    }),
    [settings]
  );

  const form = useForm<ReportFormValues>({
    resolver: zodResolver(reportFormSchema),
    defaultValues: initialValues, // Add defaultValues here
  });

  useEffect(() => {
    form.reset(initialValues);
  }, [form, initialValues]);

  const { setUnsavedSettings } = useSettingsPage();

  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const hasChanges =
        JSON.stringify(formValues) !== JSON.stringify(initialValues);
      setUnsavedSettings(hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [form, form.watch, initialValues, setUnsavedSettings]);

  function onSubmit(data: ReportFormValues) {
    setIsSubmitting(true);
    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        report_show_boundary: data.show_boundary,
        report_show_job_details: data.show_job_details_in_export,
        report_export_all: data.export_all_rows_in_report,
        report_ship_stopped_speed:
          data.interpret_ship_to_be_stopped_below_speed === ''
            ? undefined
            : data.interpret_ship_to_be_stopped_below_speed,
        report_exclude_all_non_vessels: data.enable_standard_vessel_filter,
      },
    };
    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-reports-settings-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsReportSuccessUpdateMessage,
        });
        setIsSubmitting(false);
        setUnsavedSettings(false);
      },
      onError: () => {
        toast({
          className: 'update-reports-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsReportFailedUpdateMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="space-y-4">
          <FormField
            key="show_boundary"
            control={form.control}
            name="show_boundary"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    {messages.settingsReportShowBoundaryLabel}
                  </FormLabel>
                  <FormDescription>
                    {messages.settingsReportShowBoundaryDescription}
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={(value) => {
                      field.onChange(value);
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            key="show_job_details_in_export"
            control={form.control}
            name="show_job_details_in_export"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                <div className="space-y-0.5">
                  <div className="space-x-2">
                    <FormLabel className="text-base">
                      {messages.settingsReportShowJobDetailsExportLabel}
                    </FormLabel>
                    <InfoIconTooltip>
                      {messages.settingsReportShowJobDetailsInfoIconLabel}
                    </InfoIconTooltip>
                  </div>

                  <FormDescription>
                    {messages.settingsReportShowJobDetailsExportDescription}
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={(value) => {
                      field.onChange(value);
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            key="export_all_rows_in_report"
            control={form.control}
            name="export_all_rows_in_report"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    {messages.settingsReportExportAllRowsLabel}
                  </FormLabel>
                  <FormDescription>
                    {messages.settingsReportExportAllRowsDescription}
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={(value) => {
                      field.onChange(value);
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            key="interpret_ship_to_be_stopped_below_speed"
            control={form.control}
            name="interpret_ship_to_be_stopped_below_speed"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-center justify-between border rounded-lg p-4'
                )}
              >
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    {messages.settingsReportInterpretShipToBeStoppedLabel}
                  </FormLabel>
                  <FormDescription>
                    {messages.settingsReportInterpretShipToBeStoppedDescription}{' '}
                  </FormDescription>
                </div>
                <FormControl
                  id="settings-message-lookback"
                  onChange={() => form.clearErrors()}
                >
                  <Input
                    className="w-20"
                    type="number"
                    {...field}
                    value={field.value ?? ''} // Ensure controlled value
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        field.onChange(''); // Allow empty string
                      } else {
                        const numberValue = parseFloat(value);
                        if (!isNaN(numberValue)) {
                          field.onChange(numberValue);
                        }
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            key="enable_standard_vessel_filter"
            control={form.control}
            name="enable_standard_vessel_filter"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    {messages.settingsReportEnableStandardVesselFilterLabel}
                  </FormLabel>
                  <FormDescription>
                    {
                      messages.settingsReportEnableStandardVesselFilterDescription
                    }
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={(value) => {
                      field.onChange(value);
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <div className="flex items-center justify-end gap-2 pr-2">
          <Button
            id="settings-message-save-button"
            onClick={form.handleSubmit(onSubmit)}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? messages.genericSubmitting : messages.genericSubmit}
          </Button>
        </div>
      </form>
    </Form>
  );
};
